var React = require('react');

/**
 * A close enough check to test if a value is a Synthetic Event or not.
 * See: https://github.com/reactjs/rfcs/pull/112
 * @private
 */

function isReactSyntheticEvent(value) {
  if (typeof value !== "object" || value === null) { return false; }
  if (typeof value.bubbles !== "boolean") { return false; }
  if (typeof value.cancellable !== "boolean") { return false; }
  if (typeof value.defaultPrevented !== "boolean") { return false; }
  if (typeof value.eventPhase !== "number") { return false; }
  if (typeof value.isTrusted !== "boolean") { return false; }
  if (typeof value.timestamp !== "number") { return false; }
  if (typeof value.type !== "string") { return false; }
  return true;
}

function isEmptyValue(value) {
  if (value === "") { return true; }
  if (value === null) { return true; }
  if (typeof value === "undefined") { return true; }
  return false;
}
/**
 * Call `useField()` to create a single field in a form.
 */


function useField(options) {
  var defaultValue = options.defaultValue;
  var validations = options.validations || [];
  var ref = React.useState(defaultValue);
  var value = ref[0];
  var setValue = ref[1];
  var ref$1 = React.useState([]);
  var errors = ref$1[0];
  var setErrors = ref$1[1];
  var ref$2 = React.useState(false);
  var validating = ref$2[0];
  var setValidating = ref$2[1];
  var ref$3 = React.useState(false);
  var focused = ref$3[0];
  var setFocused = ref$3[1];
  var ref$4 = React.useState(false);
  var blurred = ref$4[0];
  var setBlurred = ref$4[1];
  var ref$5 = React.useState(false);
  var touched = ref$5[0];
  var setTouched = ref$5[1];
  var ref$6 = React.useState(false);
  var dirty = ref$6[0];
  var setDirty = ref$6[1];
  var ref$7 = React.useState(options.required || false);
  var required = ref$7[0];
  var setRequired = ref$7[1];
  var ref$8 = React.useState(options.disabled || false);
  var disabled = ref$8[0];
  var setDisabled = ref$8[1];
  var ref$9 = React.useState(options.readOnly || false);
  var readOnly = ref$9[0];
  var setReadOnly = ref$9[1];
  var onFocus = React.useCallback(function () {
    setFocused(true);
    setTouched(true);
  }, []);
  var onBlur = React.useCallback(function () {
    setFocused(false);
    setBlurred(true);
  }, []);
  var valueRef = React.useRef(value);

  function reset() {
    valueRef.current = defaultValue;
    setValue(defaultValue);
    setErrors([]);
    setValidating(false);
    setBlurred(false);
    if (!focused) { setTouched(false); }
    setDirty(false);
  }

  function validate() {
    var initValue = valueRef.current;
    var errorsMap = [];
    setValidating(true);
    var promises = validations.map(function (validation, index) {
      return Promise.resolve().then(function () { return validation(initValue); }).then(function (errors) {
        errorsMap[index] = errors;

        if (Object.is(initValue, valueRef.current)) {
          setErrors(errorsMap.flat(1));
        }
      });
    });
    return Promise.all(promises).then(function () {
      if (Object.is(initValue, valueRef.current)) {
        setValidating(false);
      }
    }).catch(function (err) {
      if (Object.is(initValue, valueRef.current)) {
        setValidating(false);
      }

      throw err;
    });
  }

  var setValueHandler = function (value) {
    valueRef.current = value;
    setDirty(true);
    setValue(value);
    setErrors([]);
    validate();
  };

  var onChange = function (value) {
    if (isReactSyntheticEvent(value)) {
      throw new TypeError("Expected `field.onChange` to be called with a value, not an event");
    }

    setValueHandler(value);
  };

  var valid = !errors.length && (required ? !isEmptyValue(value) : true);
  return {
    value: value,
    errors: errors,
    defaultValue: defaultValue,
    setValue: setValueHandler,
    focused: focused,
    blurred: blurred,
    touched: touched,
    dirty: dirty,
    valid: valid,
    validating: validating,
    required: required,
    disabled: disabled,
    readOnly: readOnly,
    setRequired: setRequired,
    setDisabled: setDisabled,
    setReadOnly: setReadOnly,
    reset: reset,
    validate: validate,
    props: {
      value: value,
      required: required,
      disabled: disabled,
      readOnly: readOnly,
      onFocus: onFocus,
      onBlur: onBlur,
      onChange: onChange
    }
  };
}
/**
 * Call `useForm()` with to create a single form.
 */

function useForm(options) {
  var fields = options.fields;
  var onSubmit = options.onSubmit;
  var ref = React.useState(false);
  var submitted = ref[0];
  var setSubmitted = ref[1];
  var ref$1 = React.useState(false);
  var submitting = ref$1[0];
  var setSubmitting = ref$1[1];
  var ref$2 = React.useState([]);
  var submitErrors = ref$2[0];
  var setSubmitErrors = ref$2[1];
  var focused = fields.some(function (field) { return field.focused; });
  var touched = fields.some(function (field) { return field.touched; });
  var dirty = fields.some(function (field) { return field.dirty; });
  var valid = fields.every(function (field) { return field.valid; });
  var validating = fields.some(function (field) { return field.validating; });
  var fieldErrors = fields.reduce(function (errors, field) { return errors.concat(field.errors); }, []);

  function reset() {
    setSubmitted(false);
    setSubmitting(false);
    setSubmitErrors([]);
    fields.forEach(function (field) { return field.reset(); });
  }

  function validate() {
    return Promise.all(fields.map(function (field) { return field.validate(); })).then(function () {});
  }

  function onSubmitHandler() {
    setSubmitted(true);
    setSubmitting(true);
    return Promise.resolve().then(function () { return onSubmit(); }) // Dunno why TS is mad
    .then(function (errs) {
      setSubmitting(false);
      setSubmitErrors(errs || []);
    }).catch(function (err) {
      setSubmitting(false);
      throw err;
    });
  }

  return {
    fieldErrors: fieldErrors,
    submitErrors: submitErrors,
    submitted: submitted,
    submitting: submitting,
    focused: focused,
    touched: touched,
    dirty: dirty,
    valid: valid,
    validating: validating,
    validate: validate,
    reset: reset,
    submit: onSubmitHandler,
    props: {
      onSubmit: onSubmitHandler
    }
  };
}

exports.useField = useField;
exports.useForm = useForm;
//# sourceMappingURL=react-jeff.js.map
